import { useMemo, useState } from "react";

const inputCls =
  "w-full rounded-sm border border-pe-sand/70 bg-white px-4 py-3 text-sm text-pe-forest outline-none focus:border-pe-gold transition-colors placeholder:text-pe-muted/60";

export function isVisible(q, answers) {
  const cond = q.conditional;
  if (!cond) return true;
  const parent = answers[cond.question];
  if (cond.equals !== undefined) return parent === cond.equals;
  if (cond.not_in) return parent !== undefined && parent !== null && parent !== "" && !cond.not_in.includes(parent);
  if (cond.includes_any) return Array.isArray(parent) && cond.includes_any.some((v) => parent.includes(v));
  return true;
}

function isEmpty(v, type) {
  return v === undefined || v === null || v === "" || (type === "multi" && (!Array.isArray(v) || !v.length)) || (type === "boolean" && v !== true);
}

export function missingRequired(definition, answers) {
  return definition.questions.filter((q) => isVisible(q, answers) && q.required && isEmpty(answers[q.id], q.type));
}

export function PEFormRenderer({ definition, initial = {}, onSubmit, busy, submitLabel = "Save form", testPrefix, value, onChange, hideSubmit }) {
  const [localAnswers, setLocalAnswers] = useState(initial);
  const [error, setError] = useState("");
  const prefix = testPrefix || `pe-form-${definition.key}`;
  const controlled = value !== undefined;
  const answers = controlled ? value : localAnswers;

  const set = (qid, val) => {
    const next = { ...answers, [qid]: val };
    if (controlled) onChange(next);
    else setLocalAnswers(next);
  };
  const toggleMulti = (qid, id) => {
    const cur = Array.isArray(answers[qid]) ? answers[qid] : [];
    set(qid, cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const visibleQuestions = useMemo(() => definition.questions.filter((q) => isVisible(q, answers)), [definition, answers]);

  const submit = () => {
    const missing = visibleQuestions.filter((q) => q.required && isEmpty(answers[q.id], q.type));
    if (missing.length) {
      setError(`Please complete: ${missing.map((q) => q.label).slice(0, 4).join("; ")}${missing.length > 4 ? "…" : ""}`);
      return;
    }
    setError("");
    onSubmit(answers);
  };

  return (
    <div className="space-y-5" data-testid={`${prefix}-renderer`}>
      <p className="text-[12px] text-pe-muted leading-relaxed">{definition.intro}</p>
      {visibleQuestions.map((q) => (
        <div key={q.id}>
          <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">
            {q.label} {q.required && <span className="text-pe-brass">*</span>}
          </p>
          {q.type === "text" && (
            <input data-testid={`${prefix}-q-${q.id}`} className={inputCls} value={answers[q.id] || ""} onChange={(e) => set(q.id, e.target.value)} />
          )}
          {q.type === "textarea" && (
            <textarea data-testid={`${prefix}-q-${q.id}`} rows={2} className={inputCls} value={answers[q.id] || ""} onChange={(e) => set(q.id, e.target.value)} />
          )}
          {q.type === "single" && (
            <div className="flex flex-wrap gap-2" data-testid={`${prefix}-q-${q.id}`}>
              {q.options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  data-testid={`${prefix}-q-${q.id}-${o.id}`}
                  onClick={() => set(q.id, o.id)}
                  className={`px-3.5 py-2 rounded-full border text-[12px] transition-colors ${answers[q.id] === o.id ? "border-pe-forest bg-pe-forest text-pe-ivory" : "border-pe-sand/70 bg-white text-pe-body hover:border-pe-gold"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
          {q.type === "multi" && (
            <div className="flex flex-wrap gap-2" data-testid={`${prefix}-q-${q.id}`}>
              {q.options.map((o) => {
                const on = Array.isArray(answers[q.id]) && answers[q.id].includes(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`${prefix}-q-${q.id}-${o.id}`}
                    onClick={() => toggleMulti(q.id, o.id)}
                    className={`px-3.5 py-2 rounded-full border text-[12px] transition-colors ${on ? "border-pe-forest bg-pe-forest text-pe-ivory" : "border-pe-sand/70 bg-white text-pe-body hover:border-pe-gold"}`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          )}
          {q.type === "boolean" && (
            <label className="flex items-start gap-3 text-[13px] text-pe-body leading-relaxed cursor-pointer">
              <input data-testid={`${prefix}-q-${q.id}`} type="checkbox" className="mt-1 accent-[#C59B27]" checked={!!answers[q.id]} onChange={(e) => set(q.id, e.target.checked)} />
              <span>Yes, I confirm.</span>
            </label>
          )}
        </div>
      ))}
      {error && (
        <p data-testid={`${prefix}-error`} role="alert" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-sm px-4 py-3">
          {error}
        </p>
      )}
      {!hideSubmit && (
        <button
          data-testid={`${prefix}-submit`}
          onClick={submit}
          disabled={busy}
          className="rounded-full bg-pe-forest text-pe-ivory px-7 py-2.5 font-sans text-[10px] tracking-[0.25em] uppercase hover:bg-pe-olive transition-colors disabled:opacity-50"
        >
          {busy ? "Saving…" : submitLabel}
        </button>
      )}
    </div>
  );
}
